import React, { useEffect, useState, useMemo, useCallback } from "react";
import { handleErrorImage, viewsPercentage, filterCommonPage } from "../../../../utis/helper";
import CustomTooltip from "../../../Elements/Tooltip";
import styles from "../Viewer/Viewer.module.css";

const SessionTable = ({
  session,
  receiver,
  setSelectedPage,
  selectedPage,
  thumbs_details,
  sessions,
  sessionIndex,
  selectedFilter = "all",
}) => {
  const [filteredPages, setFilteredPages] = useState([]);
  const [tagPages, setTagPages] = useState({});
  const [pagesWithoutTags, setPagesWithoutTags] = useState([]);
  const [pageTags, setPageTags] = useState({});
  const [latestCookieStatus, setLatestCookieStatus] = useState(null);
  const elements = window?.objInit?.elements;

  // Listen for cookie consent changes to update the icon reactively
  useEffect(() => {
    const handleCookieConsentChange = (event) => {
      // Update latest cookie status from the event or window.objInit
      const newStatus = event?.detail?.cookieStatus || window?.objInit?.session?.cookie_status;
      if (newStatus) {
        setLatestCookieStatus(newStatus);
      }
    };

    // Initialize from window.objInit if available
    if (window?.objInit?.session?.cookie_status) {
      setLatestCookieStatus(window.objInit.session.cookie_status);
    }

    window.addEventListener('cookieConsentChanged', handleCookieConsentChange);
    return () => {
      window.removeEventListener('cookieConsentChanged', handleCookieConsentChange);
    };
  }, []);

  // normalize to Number (or null)
const toN = (v) => (v == null ? null : Number(v));

// const getPopMetaFor = (p) => ({
//   enter: {
//     name: p?.enter_pop_name ?? session?.enter_pop_name,
//     date: p?.enter_pop_date ?? session?.enter_pop_date,
//   },
//   exit: {
//     name: p?.exit_pop_name ?? session?.exit_pop_name,
//     date: p?.exit_pop_date ?? session?.exit_pop_date,
//   },
//   timed: {
//     name:
//       p?.flash_pop_name ?? p?.timed_pop_name ??
//       session?.flash_pop_name ?? session?.timed_pop_name,
//     date:
//       p?.flash_pop_date ?? p?.timed_pop_date ??
//       session?.flash_pop_date ?? session?.timed_pop_date,
//   },
// });



const getPopMetaFor = (p) => ({
  enter: {
    name: p?.enter_pop?.name ?? p?.enter_pop_name,
    date: p?.enter_pop?.date ?? p?.enter_pop_date,
  },
  exit: {
    name: p?.exit_pop?.name ?? p?.exit_pop_name,
    date: p?.exit_pop?.date ?? p?.exit_pop_date,
  },
  timed: {
    name: p?.flash_pop?.name ?? p?.timed_pop?.name ?? p?.flash_pop_name ?? p?.timed_pop_name,
    date: p?.flash_pop?.date ?? p?.timed_pop?.date ?? p?.flash_pop_date ?? p?.timed_pop_date,
  },
});

// Source 1: session-level fields (if present)
const sessionLevel = {
  enter: toN(session?.enter_pop?.page),
  exit: toN(session?.exit_pop?.page),
  timed: toN((session?.flash_pop || session?.timed_pop)?.page),
};

// Source 2: page-level fields (API sometimes embeds under each page)
const pageLevel = useMemo(() => {
  let enter = null, exit = null, timed = null;
  (session?.pages || []).forEach((p) => {
    enter = enter ?? toN(p?.enter_pop?.page);
    exit  = exit  ?? toN(p?.exit_pop?.page);
    const t = p?.flash_pop || p?.timed_pop;
    timed = timed ?? toN(t?.page);
  });
  return { enter, exit, timed };
}, [session?.pages]);

// Final expectations (prefer session-level, fallback to page-level)
const expectEnterPage = sessionLevel.enter ?? pageLevel.enter;
const expectExitPage  = sessionLevel.exit  ?? pageLevel.exit;
const expectTimedPage = sessionLevel.timed ?? pageLevel.timed;

// (debug)
console.group('[TABLE] Pop expectations');
console.log('enter:', expectEnterPage, 'exit:', expectExitPage, 'timed:', expectTimedPage);
console.groupEnd();


  let landingPage = session.landing_page;
  const landingPageNumber =
    landingPage?.page ?? (session?.pages?.length ? session.pages[0]?.page : null);

  const pdfFile = {
    num_pages: objInit.pdfFile.num_pages,
    file: {
      url: objInit.pdfFile.file.url,
      language: objInit.pdfFile.language,
    },
    page_tags: objInit.pdfFile.page_tags,
    thumbs_details: objInit.pdfFile.thumbs_details,
    pdfStamp: objInit.pdfStamp,
  };

  // ---- helpers to detect video carry-over across rows ----
  const msgText = (oc) => String(oc?.clicks?.message || "");
  const hasVideoStart = (ocArr = []) =>
    ocArr.some((oc) => /video/i.test(msgText(oc)) && /(play|start|started|unmute)/i.test(msgText(oc)));
  const hasVideoStop = (ocArr = []) =>
    ocArr.some((oc) => /video/i.test(msgText(oc)) && /(pause|stop|stopp|ended|mute)/i.test(msgText(oc)));

  // index of the first time recipient landed on that page (display-only)
  const landingIndex = useMemo(() => {
    if (!session?.pages?.length || landingPageNumber == null) return -1;
    return session.pages.findIndex(
      (p) => Number(p.page) === Number(landingPageNumber)
    );
  }, [session?.pages, landingPageNumber]);

const processedPages = useMemo(() => {
  if (!Array.isArray(session?.pages)) return [];
  const prepared = session.pages.map((p) => ({
    ...p,
    other_clicks: Array.isArray(p.other_clicks) ? [...p.other_clicks] : [],
    elements_imp_clicks: Array.isArray(p.elements_imp_clicks) ? [...p.elements_imp_clicks] : [],
    element_tags: Array.isArray(p.element_tags) ? [...p.element_tags] : [],
    language_editions: Array.isArray(p.language_editions) ? [...p.language_editions] : [],
  }));
  return filterCommonPage(prepared);
}, [session?.pages]);

  const mergeConsecutivePages = useCallback(
    (pagesList = []) =>
      filterCommonPage(
        pagesList.map((p) => ({
          ...p,
          other_clicks: Array.isArray(p.other_clicks) ? [...p.other_clicks] : [],
          elements_imp_clicks: Array.isArray(p.elements_imp_clicks) ? [...p.elements_imp_clicks] : [],
          element_tags: Array.isArray(p.element_tags) ? [...p.element_tags] : [],
          language_editions: Array.isArray(p.language_editions) ? [...p.language_editions] : [],
        }))
      ),
    []
  );

  const landingPageData = useMemo(() => {
    const base = landingPage || {};
    if (!processedPages?.length) return base;
    const match = processedPages.find(
      (p) => Number(p?.page) === Number(base?.page ?? landingPageNumber)
    );
    if (match) {
      return { ...base, ...match };
    }

    if (landingPageNumber != null) {
      const fallback = processedPages.find(
        (p) => Number(p?.page) === Number(landingPageNumber)
      );
      if (fallback) return { ...base, ...fallback };
    }

    return base;
  }, [landingPage, processedPages, landingPageNumber]);

  // Get the latest cookie status - prefer state (updated reactively), fallback to landing page data
  // This ensures we always show the latest cookie consent selection, not just the first one
  const currentCookieStatus = useMemo(() => {
    // Use latestCookieStatus state if available (updated when consent changes via event)
    // Fallback to landingPageData.cookie_status (from API)
    return latestCookieStatus || landingPageData?.cookie_status || landingPage?.cookie_status || null;
  }, [latestCookieStatus, landingPageData?.cookie_status, landingPage?.cookie_status]);

  // Helper function to render cookie status icon
  const renderCookieIcon = (cookieStatus, date) => {
    if (!cookieStatus) return null;
    
    const tooltipDate = date?.includes("@") ? date.split("@")[1].trim() : date;
    
    if (cookieStatus == "rejected") {
      return (
        <CustomTooltip 
          title={`Recipient rejected all cookies at ${tooltipDate || date}`}
        >
          <span>
            <svg
              width="16px"
              height="16px"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 16H9.01M12 11H12.01M7 10H7.01M15 16H15.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C12 5.76142 13.7909 8 16 8C16 10.2091 18.2386 12 21 12Z"
                stroke="#FF0000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </span>
        </CustomTooltip>
      );
    }
    
    if (cookieStatus == "essential" || cookieStatus == "essential_only") {
      return (
        <CustomTooltip 
          title={`Recipient only accepted essential cookies at ${tooltipDate || date}`}
        >
          <span>
            <svg
              width="16px"
              height="16px"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 16H9.01M12 11H12.01M7 10H7.01M15 16H15.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C12 5.76142 13.7909 8 16 8C16 10.2091 18.2386 12 21 12Z"
                stroke="#eed202"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </span>
        </CustomTooltip>
      );
    }
    
    if (cookieStatus == "acceptAll" || cookieStatus == "accepted_all") {
      return (
        <CustomTooltip 
          title={`Recipient accepted all cookies at ${tooltipDate || date}`}
        >
          <span>
            <svg
              width="16px"
              height="16px"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#5CC04D"
            >
              <path
                d="M9 16H9.01M12 11H12.01M7 10H7.01M15 16H15.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C12 5.76142 13.7909 8 16 8C16 10.2091 18.2386 12 21 12Z"
                stroke="#5CC04D"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          </span>
        </CustomTooltip>
      );
    }
    
    return null;
  };
  

console.group('[TABLE] Pop expectations');
console.log('enter_pop.page:', expectEnterPage);
console.log('exit_pop.page:', expectExitPage);
console.log('timed_pop.page:', expectTimedPage);
console.groupEnd();

  useEffect(() => {
    Object.keys(pdfFile.page_tags || {}).map(function (key) {
      pageTags[key] = [];
      if (pdfFile.page_tags[key].length === 0) {
        pagesWithoutTags.push(key);
      }
      pdfFile.page_tags[key].map(function (el) {
        if (tagPages[el.name] === undefined) {
          tagPages[el.name] = [];
        }
        pageTags[key].push(el.name);
        tagPages[el.name].push(key);
      });
    });

    setPageTags({ ...pageTags });
    setTagPages({ ...tagPages });
    setPagesWithoutTags([...pagesWithoutTags]);
  }, []);

  useEffect(() => {
    let basePages = processedPages;
    if (selectedFilter === "not_tagged") {
      basePages = processedPages.filter((el) =>
        pagesWithoutTags.includes(String(el.page))
      );
    }
    const merged = mergeConsecutivePages(basePages);
    setFilteredPages(merged);
  }, [
    selectedFilter,
    processedPages,
    pagesWithoutTags,
    mergeConsecutivePages,
    expectExitPage,
  ]);

  const filterPages = (data) => {
    const updatedFilteredPages = processedPages.filter(function (el) {
      return data.indexOf(String(el.page)) !== -1;
    });
    setFilteredPages(mergeConsecutivePages(updatedFilteredPages));
  };

  // -------- NEW: compute background-video indicator for EVERY subsequent row until stop --------
  const bgCarry = useMemo(() => {
    const res = [];
    let playing = false;
    let fromPage = null;

    (filteredPages || []).forEach((row, idx) => {
      // show icon if something was already playing from a different page
      const show = playing && row.page !== fromPage;
      res[idx] = { show, fromPage };

      // update state based on events on THIS row
      const stopHere = hasVideoStop(row.other_clicks);
      const startHere = hasVideoStart(row.other_clicks);

      // stop first (if both present, stop takes effect then start can re-arm)
      if (stopHere) {
        playing = false;
        fromPage = null;
      }
      if (startHere) {
        playing = true;
        fromPage = row.page;
      }
    });
    return res;
  }, [filteredPages]);

  return (
    <table className="w-100">
      <thead className={styles.statsTableHead}>
        <tr>
          <th></th>
          <th>Page</th>
          <th>Duration</th>
          <th>Page Tags</th>
          <th>Element Tags</th>
          <th>Actions</th>
        </tr>
      </thead>

                                 
      <tbody>
        {landingPageData?.page != null &&
          selectedFilter === "all" && (
          <>
            <tr
              onClick={() =>
                setSelectedPage({
                  id: landingPageData.page,
                })
              }
            >
              <td>
                <img
                  src={`${thumbs_details?.small}${landingPageData.page - 1}.${thumbs_details?.extension}`}
                  onError={(e) => handleErrorImage(e)}
                  alt={landingPageData.page}
                  className={
                    selectedPage?.id == landingPageData?.page ? styles.selectedImage : ""
                  }
                />
              </td>
                
              <td>{landingPageData.page}</td>
              <td>
                <CustomTooltip title={<p className="p-1 text-light">{landingPageData.time_tooltip}</p>}>
                  <span>{landingPageData.min_viewed}</span>
                </CustomTooltip>
              </td>
              <td>
                 <div className="tags-display">
                {pageTags[landingPageData.page]?.map((tag, index) => {
                  return (
                    <CustomTooltip title="Page tag" key={`${tag}-lp-${index}`}>
                      <span className="tag-primary text-white">{tag}</span>
                    </CustomTooltip>
                  );
                }) || <span className="text-muted">No tags</span>}
                </div>
              </td>
              <td>
                 <div className="tags-display">
                {landingPageData.element_tags
                  ? landingPageData.element_tags.map((tag, index) => {
                      const triggerTime = landingPageData.other_clicks?.find((oc) => oc?.trigger_time)?.trigger_time ?? landingPageData.other_clicks?.[0]?.trigger_time;
                      return triggerTime ? (
                        <CustomTooltip
                          key={`${tag}-lp-et-${index}`}
                          title={`Triggered @ ${triggerTime}`}
                        >
                          <span className="tag-primary text-white">{tag}</span>
                        </CustomTooltip>
                      ) : (
                        <span key={`${tag}-lp-et-${index}`} className="tag-primary text-white">
                          {tag}
                        </span>
                      );
                    })
                  : undefined}
                  </div>
              </td>
              <td>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <>
                    {/* Show cookie icon on landing page row if cookie_status exists */}
                    {currentCookieStatus && 
                     renderCookieIcon(currentCookieStatus, landingPageData?.date)}
                  </>


{expectEnterPage != null && toN(landingPageData.page) === expectEnterPage && (() => {
  // find the correct page object from session.pages
  const pageWithEnter = session?.pages?.find(p => toN(p.page) === expectEnterPage);
  const meta = getPopMetaFor(pageWithEnter).enter;

  return (
    <CustomTooltip 
      title={`EnterPop${ meta?.name ? ` ${meta.name}` : ''} was triggered${meta?.date ? ` @ ${meta.date}` : ''}`}
    >
      <span className="icon-position icon-lamp6 cursor-pointer"></span>
    </CustomTooltip>
  );
})()}

{expectExitPage != null && toN(landingPageData.page) === expectExitPage && (() => {
  const pageWithExit = session?.pages?.find(p => toN(p.page) === expectExitPage);
  const meta = getPopMetaFor(pageWithExit).exit;
  return (
    <CustomTooltip 
      title={`ExitPop${ meta?.name ? ` ${meta.name}` : ''} was triggered${meta?.date ? ` @ ${meta.date}` : ''}`}
    >
      <span className="icon-position icon-lamp4 cursor-pointer"></span>
    </CustomTooltip>
  );
})()}

{expectTimedPage != null && toN(landingPageData.page) === expectTimedPage && (() => {
  const pageWithTimed = session?.pages?.find(p => toN(p.page) === expectTimedPage);
  const meta = getPopMetaFor(pageWithTimed).timed;
  return (
    <CustomTooltip 
      title={`TimedPop${ meta?.name ? ` ${meta.name}` : ''} was triggered${meta?.date ? ` @ ${meta.date}` : ''}`}
    >
      <span className="icon-position icon-lamp5 cursor-pointer"></span>
    </CustomTooltip>
  );
})()}



                                       
                  {landingPageData.page === (landingPage?.landing_page ?? landingPageData.page) ? (
                    <CustomTooltip title={<>Recipient began viewing on<br />{landingPageData.date}</>}>
                      <span className="icon-position icon-enter cursor-pointer"></span>
                    </CustomTooltip>
                  ) : undefined}

                  {landingPageData.cyoa_enabled && (
                    <CustomTooltip title="CYOA enabled">
                      <span className="icon-position icon-chopper cursor-pointer"></span>
                    </CustomTooltip>
                  )}

                  {landingPageData.other_clicks?.map((click, i) => {
                    const message = click?.clicks?.message;
                    const icon = click?.clicks?.icon;
                    if (!message && !icon) return null;

                    return (
                      <React.Fragment key={`lp-oc-${i}`}>
                        <CustomTooltip title={message ?? ""}>
                          <span
                            className={`icon-position cursor-pointer ${icon ?? ""}`}
                          ></span>
                        </CustomTooltip>
                        {click?.is_reactive && (
                          <CustomTooltip title="Scrolling Enabled">
                            <span
                              className="icon-position cursor-pointer icon-mouse"
                            ></span>
                          </CustomTooltip>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {landingPageData.elements_imp_clicks?.map((click, i) => (
                    <React.Fragment key={`lp-imp-${i}`}>
                      {click.action_type === "hide" && (
                        <CustomTooltip title={`Click action is ${click.action_type}`}>
                          <span>
                            <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM8 12a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
                            </svg>
                          </span>
                        </CustomTooltip>
                      )}
                      {click.action_type === "reveal" && (
                        <CustomTooltip title={`Click action is ${click.action_type}`}>
                          <span>
                            <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM8 12a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
                              <path d="M8 5a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                            </svg>
                          </span>
                        </CustomTooltip>
                      )}
                      {click.action_type === "toggleOpacity" && (
                        <CustomTooltip title={`Click action is ${click.action_type}`}>
                          <span>
                            <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM8 12a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
                              <line x1="2" y1="2" x2="14" y2="14" stroke="currentColor" strokeWidth="2" />
                            </svg>
                          </span>
                        </CustomTooltip>
                      )}
                    </React.Fragment>
                  ))}

                  {session.used_password && sessionIndex === 0 && (
                    <CustomTooltip title="Invitation code used">
                      <span className="icon-position icon-qrcode"></span>
                    </CustomTooltip>
                  )}

                  {/* OPT-IN success icon on landing page row */}
                  {sessions[0]?.has_optin &&
                    landingPageData.page === sessions[0]?.optin_page &&
                    landingPageData.once_optin &&
                    !landingPageData.optin_reject && (
                      <CustomTooltip
                        title={`${
                          session.is_email_confirm ? "Email confirmed:" : "OPT-IN:"
                        } ${receiver?.name} ${receiver?.email} ${
                          session.optin_mail_confirm ? session.optin_mail_confirm_date || "" : sessions[0]?.optin_date || ""
                        }`}
                      >
                        <span
                          className={`icon-position tooltip-inner-custom ${
                            session.is_email_confirm ? "icon-user-blue" : "icon-user"
                          } optin-icon--success cursor-pointer`}
                        ></span>
                      </CustomTooltip>
                    )}

                  {(landingPageData.exit_page || filteredPages.length < 1) && (
                    <CustomTooltip title={`recipient stopped viewing on this page at ${landingPageData.exit_page_time}`}>
                      <span className="icon-position icon-exit cursor-pointer"></span>
                    </CustomTooltip>
                  )}
                </div>
              </td>
            </tr>
            <tr
              onClick={() =>
                setSelectedPage({
                  id: landingPageData.page,
                })
              }
            >
              <td colSpan={7} className={styles.progressTd}>
                <progress value={viewsPercentage(landingPageData.seconds, session.sec_viewed)} max="100"></progress>
              </td>
            </tr>
          </>
        )}

        {filteredPages.map((sessPage, i) => {


  if (expectExitPage && expectExitPage === sessPage.page) {
    console.info('[TABLE] rendering EXIT POP icon on page row:', sessPage.page);
  }

          const isFirstLandingRow = i === landingIndex && sessionIndex === 0 && selectedFilter === "all";
          if (isFirstLandingRow) return null;

          const bg = bgCarry[i]; // { show, fromPage }
          return (
            <React.Fragment key={`row-${i}-${sessPage.page}`}>
              <tr
                onClick={() =>
                  setSelectedPage({
                    id: sessPage.page,
                  })
                }
              >
                <td>
                  <img
                    src={`${thumbs_details?.small}${sessPage.page - 1}.${thumbs_details?.extension}`}
                    onError={(e) => handleErrorImage(e)}
                    alt={sessPage.page}
                    className={selectedPage?.id == sessPage?.page ? styles.selectedImage : ""}
                  />
                </td>
                <td>{sessPage.page}</td>
                <td>
                  <CustomTooltip title={<p className="p-1 text-light">{sessPage.time_tooltip}</p>}>
                    <span>{sessPage.min_viewed}</span>
                  </CustomTooltip>
                </td>
                <td>
                  <div className="tags-display">
                  {pageTags[sessPage.page]?.map((tag, index) => {
                    return (
                      <CustomTooltip title="Page tag" key={`${tag}-p-${i}-${index}`}>
                        <span className="tag-primary text-white">{tag}</span>
                      </CustomTooltip>
                    );
                  })}
                  </div>
                </td>
                <td>
                   <div className="tags-display">
                  {sessPage.element_tags
                    ? sessPage.element_tags.map((tag, index) => {
                        const triggerTime = sessPage.other_clicks?.find((oc) => oc?.trigger_time)?.trigger_time ?? sessPage.other_clicks?.[0]?.trigger_time;
                        return triggerTime ? (
                          <CustomTooltip key={`${tag}-et-${i}-${index}`} title={`Triggered @ ${triggerTime}`}>
                            <span className="tag-primary text-white">{tag}</span>
                          </CustomTooltip>
                        ) : (
                          <span key={`${tag}-et-${i}-${index}`} className="tag-primary text-white">
                            {tag}
                          </span>
                        );
                      })
                    : undefined}
                    </div>
                </td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {sessPage.elements_imp_clicks.map((imp_clk, index) => {
                      return (
                        <CustomTooltip key={`imp-${i}-${index}`} title={imp_clk.kdf_to_kdf}>
                          <span className="icon-position cursor-pointer icon-touch" target="_blank"></span>
                        </CustomTooltip>
                      );
                    })}

                    {sessPage.cyoa_enabled && Number(sessPage.page) === Number(landingPageNumber) && (
                      <CustomTooltip title="CYOA enabled">
                        <span className="icon-position icon-chopper cursor-pointer"></span>
                      </CustomTooltip>
                    )}

                    {/* Show cookie icon on page 1 row (not just landing page) if cookie_status exists */}
                    {/* This ensures the icon appears when user navigates back to page 1 after changing consent */}
                    {currentCookieStatus && 
                     Number(sessPage.page) === 1 &&
                     renderCookieIcon(currentCookieStatus, sessPage.date)}

                  {/* background video indicator spanning across pages until a stop */}
                  {/* {bg?.show && (
                    <CustomTooltip title={`Video from page ${bg.fromPage} playing in background`}>
                      <span className="icon-position cursor-pointer icon-volume-medium"></span>
                    </CustomTooltip>
                  )} */}

                {/* {expectEnterPage != null && toN(sessPage.page) === expectEnterPage && (() => {
                  const meta = getPopMetaFor(sessPage).enter;
                  const metaName =  meta?.name;
                  if (!metaName) {
                  metaName = filteredPages[0]?.enter_pop_name
                  }

                  return (
                    <CustomTooltip title={`EnterPop${ metaName ? ` ${metaName}` : ''} was triggered at ${meta?.date ? ` @ ${meta.date}` : ''}`}>
                      <span className={`icon-position icon-lamp6 cursor-pointer ${session.enter_pop}`}></span>
                    </CustomTooltip>
                  );
                })()}

                {expectExitPage != null && toN(sessPage.page) === expectExitPage && (() => {
                  const meta = getPopMetaFor(sessPage).exit;
                  return (
                    <CustomTooltip title={`ExitPop${meta?.name ? ` ${meta.name}` : ''} was triggered at ${meta?.date ? ` @ ${meta.date}` : ''}`}>
                      <span className="icon-position icon-lamp4 cursor-pointer"></span>
                    </CustomTooltip>
                  );
                })()}

                {expectTimedPage != null && toN(sessPage.page) === expectTimedPage && (() => {
                  const meta = getPopMetaFor(sessPage).timed;
                  return (
                    <CustomTooltip title={`TimedPop${meta?.name ? ` ${meta.name}` : ''} was triggered at ${meta?.date ? ` @ ${meta.date}` : ''}`}>
                      <span className="icon-position icon-lamp5 cursor-pointer"></span>
                    </CustomTooltip>
                  );
                })()} */}

                  {expectEnterPage != null && toN(sessPage.page) === expectEnterPage && (() => {
                    const pageWithEnter = session?.pages?.find(p => toN(p.page) === expectEnterPage);
                    const meta = getPopMetaFor(pageWithEnter).enter;
                    return (
                      <CustomTooltip title={`EnterPop${ meta?.name ? ` ${meta.name}` : ''} was triggered${meta?.date ? ` @ ${meta.date}` : ''}`}>
                        <span className="icon-position icon-lamp6 cursor-pointer"></span>
                      </CustomTooltip>
                    );
                  })()}

                  {expectExitPage != null && toN(sessPage.page) === expectExitPage && (() => {
                    const pageWithExit = session?.pages?.find(p => toN(p.page) === expectExitPage);
                    const meta = getPopMetaFor(pageWithExit).exit;
                    return (
                      <CustomTooltip title={`ExitPop${ meta?.name ? ` ${meta.name}` : ''} was triggered${meta?.date ? ` @ ${meta.date}` : ''}`}>
                        <span className="icon-position icon-lamp4 cursor-pointer"></span>
                      </CustomTooltip>
                    );
                  })()}

                  {expectTimedPage != null && toN(sessPage.page) === expectTimedPage && (() => {
                    const pageWithTimed = session?.pages?.find(p => toN(p.page) === expectTimedPage);
                    const meta = getPopMetaFor(pageWithTimed).timed;
                    return (
                      <CustomTooltip title={`TimedPop${ meta?.name ? ` ${meta.name}` : ''} was triggered${meta?.date ? ` @ ${meta.date}` : ''}`}>
                        <span className="icon-position icon-lamp5 cursor-pointer"></span>
                      </CustomTooltip>
                    );
                  })()}

                  {sessPage.page === sessPage.download_page && (
                    <CustomTooltip title="KDF downloaded">
                      <span className="cursor-pointer icon-download"></span>
                    </CustomTooltip>
                  )}

                  {/* {session.is_optin_session &&
                    sessions[0]?.has_optin &&
                    sessPage.page === sessions[0]?.optin_page &&
                    sessPage.once_optin && (
                      <CustomTooltip title={sessions[0]?.custom_text}>
                        <span className="icon-position cursor-help icon-clipboard"></span>
                      </CustomTooltip>
                    )} */}

                  {sessPage.other_clicks?.map((oc, k) => {
                    const icon = oc?.clicks?.icon;
                    const message = oc?.clicks?.message;
                    const visit_url = oc?.visit_page_url;

                    if (!icon && !message) return null;

                    const IconElement = (
                        <span
                            className={`icon-position cursor-pointer ${icon ?? ""}`}
                        ></span>
                    );

                    return (
                      <React.Fragment key={`oc-${i}-${k}`}>
                        <CustomTooltip
                          title={
                            <p
                              className="p-1"
                              dangerouslySetInnerHTML={{ __html: message ?? "" }}
                            />
                    }
                        >
                        {visit_url ? (
                           <a
                               href={visit_url}
                               target="_blank"
                               rel="noopener noreferrer"
                               style={{ textDecoration: "none" }}
                           >
                               {IconElement}
                           </a>
                        ) : (
                            IconElement
                        )}
                        </CustomTooltip>
                        {oc?.is_reactive ? (
                          <CustomTooltip title="Scrolling enabled">
                            <span
                              className="icon-position cursor-pointer icon-mouse"
                            ></span>
                          </CustomTooltip>
                        ) : null}
                      </React.Fragment>
                    );
                  })}

                  {/* OPT-IN success icon */}
                  {sessions[0]?.has_optin &&
                    sessPage.page === sessions[0]?.optin_page &&
                    sessPage.once_optin &&
                    !sessPage.optin_reject && (
                      <CustomTooltip
                        title={`${
                          session.is_email_confirm ? "Email confirmed:" : "OPT-IN:"
                        } ${receiver?.name} ${receiver?.email} ${
                          session.optin_mail_confirm ? session.optin_mail_confirm_date || "" : sessions[0]?.optin_date || ""
                        }`}
                      >
                        <span
                          className={`icon-position tooltip-inner-custom ${
                            session.is_email_confirm ? "icon-user-blue" : "icon-user"
                          } optin-icon--success cursor-pointer`}
                        ></span>
                      </CustomTooltip>
                    )}

                  {/* OPT-IN reject icon */}
                  {sessPage.optin_reject && (
                    <CustomTooltip title="User rejected the opt-in">
                      <span
                        className={`icon-position tooltip-inner-custom ${
                          session.is_email_confirm ? "icon-user-blue" : "icon-user"
                        } optin-icon--reject cursor-pointer`}
                      ></span>
                    </CustomTooltip>
                  )}

                  {sessPage.session_pause_time && sessPage.show_pause_time && (
                    <CustomTooltip title={sessPage.session_pause_time_tooltip}>
                      <span className="icon-position icon-hour-glass2 cursor-pointer"></span>
                    </CustomTooltip>
                  )}

                  {sessPage.language_editions.map((language, li) => {
                    return (
                      <CustomTooltip key={`lang-${i}-${li}`} title={`Changed Language to { pe['page_edition'].name }`}>
                        <span className="{{pe['page_edition'].class}} cursor-pointer icon-position"></span>
                      </CustomTooltip>
                    );
                  })}

                  {session.used_password && sessionIndex === 0 && (
                    <CustomTooltip title="Invitation code used">
                      <span className="icon-position icon-qrcode"></span>
                    </CustomTooltip>
                  )}

                    {sessPage.exit_page && (
                      <CustomTooltip title={`recipient stopped viewing on this page at ${sessPage.exit_page_time}`}>
                        <span className="icon-position icon-exit cursor-pointer"></span>
                      </CustomTooltip>
                    )}
                  </div>
                </td>
              </tr>
              <tr
                onClick={() =>
                  setSelectedPage({
                    id: sessPage.page,
                  })
                }
              >
                <td colSpan={7} className={styles.progressTd}>
                  <progress value={viewsPercentage(sessPage.seconds, session.sec_viewed)} max="100"></progress>
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

export default SessionTable;
